import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text, Button, Icon, Badge, DrillChevron, EmptyState, Skeleton, StatsWrap, StatCell } from '@/nova/components';
import { MarketingGlyph } from '@/nova/layouts/MainLayout/components/MarketingGlyph';
import type { AffiliateCampaign } from '../../types';
import { isCampaignActive, buildCampaignStats, type CampaignStat } from '../../mapping';
import { CAMPAIGN_FILTERS, type CampaignFilter } from '../../constants';
import { isOpenableHttpUrl } from '../../url-safety';
import { sameDestination } from '@/nova/pages/creatives/tracking-link';
import { Notice } from '../Notice/Notice';
import { HeaderRow, HeaderLeft, HeaderTitle, Count, Panel, AffList, AffCard, AffTop, AffName, BoxedStatGrid } from '../../affiliates.styles';
// The filter chips, search control and list-card animation are the acquisition Campaigns browser's
// shared vocabulary (the same set the Affiliates tab reuses, so the two tabs read identically).
import {
  BrowserHead,
  FilterLbl,
  CategoryChips,
  CategoryPill,
  SearchSlot,
  SearchControl,
  SearchIconBtn,
  SearchDivider,
  SearchDismiss,
  SearchRow,
  SearchPill,
  SearchField,
  SearchClear,
  SearchResults,
} from '@/nova/pages/acquisition/components/Campaigns/CampaignBrowser.styles';
import { Cell } from '@/nova/pages/acquisition/components/Campaigns/Campaigns.styles';
import { Row, Col, Thumb, ThumbImg, RightGroup, Meta, UrlText, Dates } from './CampaignsTab.styles';
import { campaignsPanelState, filterCampaigns } from './CampaignsTab.state';

/**
 * A campaign thumbnail: the Ad Service key-art when we have a valid URL for it, otherwise a premium
 * gradient placeholder with a megaphone glyph. A broken image (load error) falls back to the same
 * placeholder, so the row is never left with a broken `<img>`.
 */
const CampaignThumb = ({ art }: { art?: string }): React.ReactElement => {
  const [errored, setErrored] = React.useState(false);
  const showImg = Boolean(art) && !errored;
  return (
    <Thumb>
      {showImg ? <ThumbImg src={art} alt="" loading="lazy" onError={() => setErrored(true)} /> : <MarketingGlyph size={26} />}
    </Thumb>
  );
};

export type CampaignsTabProps = {
  campaigns: AffiliateCampaign[];
  loading: boolean;
  /** Active/paused status filter (defaults to `active` upstream, so paused campaigns are hidden). */
  filter: CampaignFilter;
  onFilterSelect: (value: CampaignFilter) => void;
  /** Ad Service key-art keyed by the campaign's `external_ref` (= Ad Service campaign_id), for the
   *  card thumbnail. Absent entries (manual / video-ad / no-art campaigns) fall back to a placeholder. */
  artByRef?: Map<string, string>;
  /** Pictures by destination: a campaign whose landing URL is one of the brand's landing pages shows that page's art
   *  (used when `artByRef` has none for it). */
  artByUrl?: ReadonlyArray<{ url: string; imageUrl?: string }>;
  /** A settled fetch failure, so a failed load shows a retry instead of a false "no campaigns". */
  error?: string;
  /** Re-run the campaigns fetch (wired to the resource's `reload`). */
  onRetry?: () => void;
  /** Per-campaign Regs/FTDs activity by campaign id (from useCampaignStats). A loaded campaign has an
   *  entry (real figures, zeros at no activity); an absent id means the stats read hasn't settled/failed. */
  statsById: Record<string, CampaignStat>;
  /** True once the stats read settled, so cards show real figures (and skeletons only while loading). */
  statsLoaded: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
};

export const CampaignsTab = ({
  campaigns,
  loading,
  filter,
  onFilterSelect,
  artByRef,
  artByUrl,
  error,
  onRetry,
  statsById,
  statsLoaded,
  onCreate,
  onOpen,
}: CampaignsTabProps): React.ReactElement => {
  const { t } = useTranslation();
  // Literal-keyed so the i18n extractor harvests each label (status chips + the campaign stat tiles).
  const filterLabels: Record<string, string> = { Active: t('Active'), Paused: t('Paused'), All: t('All') };
  const statLabels: Record<string, string> = { Regs: t('Regs'), FTDs: t('FTDs'), 'Reg → FTD': t('Reg → FTD') };

  // Search is local (same pattern as the Affiliates tab): a toggle that only shows/hides the field, and
  // the term that actually filters. The term keeps filtering even while the field is collapsed.
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  const toggleSearch = React.useCallback(() => setSearchOpen((open) => !open), []);
  const clearSearch = React.useCallback(() => {
    setQuery('');
    setSearchOpen(false);
  }, []);

  // Focus the field when it opens so the owner can type immediately (external system: the DOM node).
  React.useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // Filter by status first (URL-driven), then narrow by the search term (name / landing URL).
  const byStatus = filterCampaigns(campaigns, filter);
  const q = query.trim().toLowerCase();
  const visible = q
    ? byStatus.filter((c) => c.name.toLowerCase().includes(q) || (c.landing_url ?? '').toLowerCase().includes(q))
    : byStatus;
  const state = campaignsPanelState({
    loading,
    hasError: Boolean(error),
    total: campaigns.length,
    visible: visible.length,
  });

  const dateLabel = (campaign: AffiliateCampaign): string | null => {
    if (campaign.start_date && campaign.end_date) return `${campaign.start_date} → ${campaign.end_date}`;
    if (campaign.start_date) return t('From {{date}}', { date: campaign.start_date });
    if (campaign.end_date) return t('Until {{date}}', { date: campaign.end_date });
    return null;
  };

  return (
    <>
      <HeaderRow>
        <HeaderLeft>
          <HeaderTitle text="Campaigns" tag="span" />
          <Count>{loading ? <Skeleton width="2ch" height="0.8em" radius={5} /> : campaigns.length}</Count>
        </HeaderLeft>
        <Button label={t('Create')} variant="primary" icon="plus" onClick={onCreate} />
      </HeaderRow>

      <Panel>
        {state === 'error' ? (
          <Notice
            icon="warning"
            title={t('Could not load campaigns')}
            body={error}
            actionLabel={onRetry ? t('Retry') : undefined}
            onAction={onRetry}
          />
        ) : state === 'empty' ? (
          <EmptyState text={t('No campaigns yet')} />
        ) : (
          <>
            <BrowserHead>
              <FilterLbl text="Status" tag="span" />
              <CategoryChips>
                {CAMPAIGN_FILTERS.map((option) => (
                  <CategoryPill
                    key={option.value}
                    type="button"
                    $on={filter === option.value}
                    aria-pressed={filter === option.value}
                    // Clicking the active chip returns to "All" (matches the Affiliates tab).
                    onClick={() => onFilterSelect(filter === option.value ? 'all' : option.value)}
                  >
                    <Text text={filterLabels[option.label] ?? option.label} tag="span" />
                  </CategoryPill>
                ))}
              </CategoryChips>
              <SearchSlot>
                <SearchControl $on={Boolean(q)}>
                  <SearchIconBtn
                    type="button"
                    aria-label={searchOpen ? t('Hide search') : t('Search campaigns')}
                    aria-expanded={searchOpen}
                    onClick={toggleSearch}
                  >
                    <Icon name="search" size={16} />
                  </SearchIconBtn>
                  {!searchOpen && q ? (
                    <>
                      <SearchDivider aria-hidden="true" />
                      <SearchDismiss type="button" aria-label={t('Clear search')} onClick={clearSearch}>
                        <Icon name="close" size={16} />
                      </SearchDismiss>
                    </>
                  ) : null}
                </SearchControl>
              </SearchSlot>
            </BrowserHead>

            {searchOpen ? (
              <SearchRow>
                <SearchPill>
                  <Icon name="search" size={18} />
                  <SearchField
                    ref={searchRef}
                    type="text"
                    aria-label={t('Search campaigns')}
                    placeholder={t('Search campaigns…')}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') clearSearch();
                    }}
                  />
                  {query ? (
                    <SearchClear
                      type="button"
                      aria-label={t('Clear search text')}
                      onClick={() => {
                        setQuery('');
                        searchRef.current?.focus();
                      }}
                    >
                      <Icon name="close" size={14} />
                    </SearchClear>
                  ) : null}
                </SearchPill>
                {q ? <SearchResults text="{{count}} results" placeholders={{ count: visible.length }} tag="span" /> : null}
              </SearchRow>
            ) : null}

            {state === 'empty-filtered' ? (
              // Gate on the panel state (which is false while loading), not visible.length, so a cold
              // load of ?tab=campaigns doesn't flash a false "no matches" before any campaign arrives.
              <EmptyState text={q ? t('No campaigns match your search') : t('No campaigns match this filter')} />
            ) : (
              // Re-key by the active filter so the whole list re-runs the card-enter animation on a filter
              // change (like the Affiliates tab). Search narrowing keeps the key, so typing only animates
              // newly-matching rows in rather than re-shuffling the survivors on each keystroke.
              <AffList key={filter}>
                {visible.map((campaign) => {
                  const dates = dateLabel(campaign);
                  const active = isCampaignActive(campaign);
                  // Ad Service key-art for imported campaigns (external_ref === Ad Service campaign_id), else the
                  // art of the landing page the campaign sends players to; scheme-guarded; anything else (manual,
                  // video-ad, no art) falls back to the placeholder.
                  const rawArt =
                    (campaign.external_ref ? artByRef?.get(campaign.external_ref) : undefined) ??
                    artByUrl?.find((d) => !!d.imageUrl && sameDestination(d.url, campaign.landing_url))?.imageUrl;
                  const art = rawArt && isOpenableHttpUrl(rawArt) ? rawArt : undefined;
                  return (
                    <Cell key={campaign.id}>
                      <AffCard
                        role="button"
                        tabIndex={0}
                        aria-label={t('Open {{name}}', { name: campaign.name })}
                        onClick={() => onOpen(campaign.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onOpen(campaign.id);
                          }
                        }}
                      >
                        <Row>
                          <CampaignThumb art={art} />
                          <Col>
                            <AffTop>
                              <AffName text={campaign.name} tag="span" />
                              <RightGroup>
                                <Badge label={active ? t('Active') : t('Paused')} tone={active ? 'success' : 'neutral'} />
                                <DrillChevron />
                              </RightGroup>
                            </AffTop>
                            <Meta>
                              {campaign.landing_url ? <UrlText text={campaign.landing_url} tag="span" /> : null}
                              {dates ? <Dates text={dates} tag="span" /> : null}
                            </Meta>
                          </Col>
                        </Row>
                        <StatsWrap>
                          <BoxedStatGrid $columns={3}>
                            {buildCampaignStats(statsById[campaign.id]).map((stat) => (
                              <StatCell
                                key={stat.id}
                                label={statLabels[stat.label] ?? stat.label}
                                value={stat.value}
                                tone={stat.tone}
                                loading={!statsLoaded}
                              />
                            ))}
                          </BoxedStatGrid>
                        </StatsWrap>
                      </AffCard>
                    </Cell>
                  );
                })}
              </AffList>
            )}
          </>
        )}
      </Panel>
    </>
  );
};

export default CampaignsTab;
